import { useEffect, useMemo, useState } from 'react'
import {
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
} from '@app-store'
import { CustomButton, DropdownType, SelectDropDown } from '@ui-kit'
import { RECORD_STATUS } from '../../constants/form.constants'
import {
  DUPLICATE_POPUP,
  ENTITY_IMPORTANT_KEYS,
  ENTITY_NAME,
  FILTER_OPERATIONS,
  NAMESPACES,
  PHARMACY_TRIAGE_CONSTANTS,
} from '../../utils/constants'

interface DedupeOutcomeDetailsStepProps {
  serviceId?: string | number
  subServiceId?: string | number
  onBack: () => void
  onCancel: () => void
  onSave: (payload: {
    outcomeId: string
    outcomeReasonId: string
    outcomeReasonLabel: string
  }) => void
  isSaving?: boolean
}

interface OutcomeListingItem {
  id: number | string
  name: string
  record_status?: string
}

const mapEntityOptions = (
  items: { id: number | string; name: string; record_status?: string }[] = []
): DropdownType<string>[] =>
  items
    .filter((item) => item.record_status !== RECORD_STATUS.INACTIVE)
    .map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

const getListingContent = <T,>(
  response: { content?: T[] } | T[] | undefined
): T[] => {
  if (Array.isArray(response)) {
    return response
  }

  return response?.content ?? []
}

export const DedupeOutcomeDetailsStep = ({
  serviceId,
  subServiceId,
  onBack,
  onCancel,
  onSave,
  isSaving = false,
}: DedupeOutcomeDetailsStepProps) => {
  const [outcomeOptions, setOutcomeOptions] = useState<DropdownType<string>[]>(
    []
  )
  const [outcomeReasonOptions, setOutcomeReasonOptions] = useState<
    DropdownType<string>[]
  >([])
  const [selectedOutcomeId, setSelectedOutcomeId] = useState('')
  const [selectedOutcomeReasonId, setSelectedOutcomeReasonId] = useState('')
  const [isLoadingOutcomes, setIsLoadingOutcomes] = useState(false)
  const [isLoadingOutcomeReasons, setIsLoadingOutcomeReasons] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const loadOutcomes = async () => {
      if (!serviceId || !subServiceId) {
        setOutcomeOptions([])
        setSelectedOutcomeId('')
        setSelectedOutcomeReasonId('')
        return
      }

      setIsLoadingOutcomes(true)
      setSelectedOutcomeId('')
      setSelectedOutcomeReasonId('')

      try {
        const payload: GetGenericEntityListingApiType = {
          entity_name: ENTITY_NAME.OUTCOMES,
          select: [
            'name',
            'c_sub_services',
            'services',
            'service',
            'sub_service',
          ],
          filters: [
            {
              logical_operator: 'AND',
              criteria: [],
            },
          ],
          related_entities: {
            service: {
              select: [ENTITY_IMPORTANT_KEYS.ID],
              criteria: [],
              filters: [
                {
                  criteria: [
                    {
                      values: [String(serviceId)],
                      operation: FILTER_OPERATIONS.EQUALS,
                      field_name: ENTITY_IMPORTANT_KEYS.ID,
                    },
                  ],
                },
              ],
            },
            sub_service: {
              select: [ENTITY_IMPORTANT_KEYS.ID],
              criteria: [],
              filters: [
                {
                  criteria: [
                    {
                      values: [String(subServiceId)],
                      operation: FILTER_OPERATIONS.EQUALS,
                      field_name: ENTITY_IMPORTANT_KEYS.ID,
                    },
                  ],
                },
              ],
            },
          },
          page: 0,
          size: 10000,
        }

        const response = await getGenericEntityListingAPI(payload)
        const options = mapEntityOptions(
          getListingContent<OutcomeListingItem>(response)
        ).filter((item) => item.label === DUPLICATE_POPUP.CANCELED_CASE_OUTCOME)

        if (isCancelled) {
          return
        }

        setOutcomeOptions(options)

        const defaultOutcome = options.find((item) =>
          item.label
            ?.toLowerCase()
            .includes(DUPLICATE_POPUP.CANCELED_CASE_OUTCOME.toLowerCase())
        )

        if (defaultOutcome?.value) {
          setSelectedOutcomeId(defaultOutcome.value)
        }
      } catch (error) {
        console.error(error)
        if (!isCancelled) {
          setOutcomeOptions([])
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingOutcomes(false)
        }
      }
    }

    void loadOutcomes()

    return () => {
      isCancelled = true
    }
  }, [serviceId, subServiceId])

  useEffect(() => {
    let isCancelled = false

    const loadOutcomeReasons = async () => {
      if (!selectedOutcomeId) {
        setOutcomeReasonOptions([])
        setSelectedOutcomeReasonId('')
        return
      }

      setIsLoadingOutcomeReasons(true)

      try {
        const payload: GetGenericEntityListingApiType = {
          entity_name: ENTITY_NAME.OUTCOME_REASONS,
          select: ['name', 'c_sub_services', 'services', 'outcome'],
          filters: [
            {
              logical_operator: 'AND',
              criteria: [],
            },
          ],
          related_entities: {
            outcome: {
              select: [ENTITY_IMPORTANT_KEYS.ID],
              criteria: [],
              filters: [
                {
                  criteria: [
                    {
                      values: [selectedOutcomeId],
                      operation: FILTER_OPERATIONS.EQUALS,
                      field_name: ENTITY_IMPORTANT_KEYS.ID,
                    },
                  ],
                },
              ],
            },
          },
          page: 0,
          size: 10000,
        }

        const response = await getGenericEntityListingAPI(payload)
        const options = mapEntityOptions(
          getListingContent<OutcomeListingItem>(response)
        )

        if (isCancelled) {
          return
        }

        setOutcomeReasonOptions(options)
        setSelectedOutcomeReasonId('')
      } catch (error) {
        console.error(error)
        if (!isCancelled) {
          setOutcomeReasonOptions([])
          setSelectedOutcomeReasonId('')
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingOutcomeReasons(false)
        }
      }
    }

    void loadOutcomeReasons()

    return () => {
      isCancelled = true
    }
  }, [selectedOutcomeId])

  const selectedOutcomeReasonLabel = useMemo(
    () =>
      outcomeReasonOptions.find(
        (option) => option.value === selectedOutcomeReasonId
      )?.label ?? '',
    [outcomeReasonOptions, selectedOutcomeReasonId]
  )

  const handleOutcomeChange = (value: DropdownType<string>) => {
    setSelectedOutcomeId(value.value?.toString() ?? '')
    setSelectedOutcomeReasonId('')
  }

  const handleSave = () => {
    if (!selectedOutcomeId || !selectedOutcomeReasonId) {
      return
    }

    onSave({
      outcomeId: selectedOutcomeId,
      outcomeReasonId: selectedOutcomeReasonId,
      outcomeReasonLabel: selectedOutcomeReasonLabel,
    })
  }

  return (
    <div className="bg-accent-0 flex flex-col h-full">
      <div className="flex justify-between items-center pb-2">
        <div className="flex items-center text-sm font-primary font-medium">
          <button
            type="button"
            className="cursor-pointer"
            onClick={onBack}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {DUPLICATE_POPUP.DUPLICATES_FOUND}
          </button>
          <span className="text-primary-500">
            {` / ${DUPLICATE_POPUP.OUTCOME_DETAILS}`}
          </span>
        </div>
        <div className="flex gap-2.5">
          <CustomButton onClick={onCancel} disabled={isSaving}>
            {NAMESPACES.CANCEL_BUTTON}
          </CustomButton>
          <CustomButton
            isFilled
            onClick={handleSave}
            disabled={
              isSaving ||
              isLoadingOutcomes ||
              isLoadingOutcomeReasons ||
              !selectedOutcomeId ||
              !selectedOutcomeReasonId
            }
          >
            {NAMESPACES.SAVE_BUTTON}
          </CustomButton>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <SelectDropDown
          key={`dedupe_outcome_${selectedOutcomeId}`}
          label={NAMESPACES.OUTCOME}
          options={outcomeOptions}
          defaultValue={selectedOutcomeId}
          name="dedupe_outcome"
          onChange={handleOutcomeChange}
          required
          isCrossable={false}
          disabled={true}
        />
        <SelectDropDown
          key={`dedupe_outcome_reason_${selectedOutcomeId}_${selectedOutcomeReasonId}`}
          label={PHARMACY_TRIAGE_CONSTANTS.OUTCOME_REASON_LABEL}
          options={outcomeReasonOptions}
          defaultValue={selectedOutcomeReasonId}
          name="dedupe_outcome_reason"
          onChange={(value) =>
            setSelectedOutcomeReasonId(value.value?.toString() ?? '')
          }
          required
          isCrossable={false}
          disabled={isSaving || isLoadingOutcomeReasons || !selectedOutcomeId}
          placeholder={NAMESPACES.SELECT_OPTION}
        />
      </div>
    </div>
  )
}
